import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { saveAs } from "file-saver";

import { AsyncFormSelect, SelectOption } from "src/components/common/AsyncSelect";
import ServerFooter from "src/components/common/ServerFooter";
import {
    myAxios,
    boolAndReducer,
    MyDate,
    getFileNameFromUrl,
} from "src/components/common/functions";

type JobTagQuery = {
    start: string;
    end: string;
    userId: string;
};

type JobTagRow = {
    projectName: string;
    category: string;
    laborCategory: string;
    department: string;
    assignmentSubmitTime: string;
    startTime: string;
    endTime: string;
    allDay: boolean;
    jobTagFileId: string;
    laborName: string;
    assignmentStatus: string;
    projectId: string;
    workId: string;
    assignmentId: string;
    [key: string]: unknown;
};

function isSelectOption(value: unknown): value is SelectOption {
    return value !== null &&
        typeof value === "object" &&
        "value" in value &&
        "label" in value &&
        typeof (value as SelectOption).value === "string" &&
        typeof (value as SelectOption).label === "string";
}

export default function JobTags() {
    const [searchParams, setSearchParams] = useSearchParams();
    const defaultStartDate = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}-01`;
    const defaultEndDate = new MyDate().s();
    const selectedUserId = searchParams.get("userId") ?? "";
    const selectedStart = searchParams.get("start") ?? "";
    const selectedEnd = searchParams.get("end") ?? "";

    const defaultSelectedDate: JobTagQuery = {
        start: defaultStartDate,
        end: defaultEndDate,
        userId: "",
    };

    const [currentData, setCurrentData] = useState<JobTagRow[]>([]);
    const [query, setQuery] = useState<JobTagQuery>({
        start: searchParams.get("start") ?? defaultSelectedDate.start,
        end: searchParams.get("end") ?? defaultSelectedDate.end,
        userId: searchParams.get("userId") ?? "",
    });
    const [technician, setTechnician] = useState<SelectOption | null>(null);
    const [filterWords, setFilterWords] = useState("");
    const page = Math.max(1, Number(searchParams.get("page") ?? 1) || 1);
    const limit = Math.max(1, Number(searchParams.get("limit") ?? 10) || 10);

    const filteredData = useMemo(
        () =>
            currentData.filter(row =>
                filterWords
                    .toLowerCase()
                    .split(" ")
                    .filter(Boolean)
                    .map(word => JSON.stringify(Object.values(row)).toLowerCase().includes(word))
                    .reduce(boolAndReducer, true)
            ),
        [currentData, filterWords]
    );

    useEffect(() => {
        setQuery({
            start: selectedStart || defaultStartDate,
            end: selectedEnd || defaultEndDate,
            userId: selectedUserId,
        });
    }, [defaultEndDate, defaultStartDate, selectedEnd, selectedStart, selectedUserId]);

    const currentPageProjects = useMemo(() => {
        const indexOfLastProject = page * limit;
        const indexOfFirstProject = indexOfLastProject - limit;
        return filteredData.slice(indexOfFirstProject, indexOfLastProject);
    }, [filteredData, limit, page]);

    const selectedTechnician = isSelectOption(technician) ? technician : null;

    useEffect(() => {
        if (!selectedUserId || !selectedStart || !selectedEnd) {
            setCurrentData([]);
            return;
        }
        let cancelled = false;
        const load = async () => {
            const formData = new FormData();
            formData.append("userId", selectedUserId);
            formData.append("start", selectedStart);
            formData.append("end", selectedEnd);
            const res = await myAxios({ fileName: "jobTags/readJobtags.php", formData });
            if (cancelled) return;
            if (!res) return;
            setCurrentData((res.data as JobTagRow[]) ?? []);
        };
        void load();
        return () => {
            cancelled = true;
        };
    }, [selectedEnd, selectedStart, selectedUserId]);

    const handleLoad = () => {
        if (!query.userId) {
            window.alert("Please select Technician.");
            return;
        }
        if (query.start === "") {
            window.alert("Please select start date.");
            return;
        }
        if (query.end === "") {
            window.alert("Please select end date.");
            return;
        }
        if (query.start > query.end) {
            window.alert("Start date is greater than end date.");
            return;
        }
        const params = new URLSearchParams(searchParams);
        params.set("userId", query.userId);
        params.set("start", query.start);
        params.set("end", query.end);
        params.set("page", "1");
        params.set("limit", searchParams.get("limit") ?? "10");
        setSearchParams(params, { replace: false });
    };

    const handleDownloadAll = async () => {
        if (!window.confirm("Are you sure to download all Job Tags?")) return;
        if (!currentData.length) {
            window.alert("No Job Tags Selected. Select Job Tags By Entering `Start Date` and `End Date` Above.");
            return;
        }
        if (currentData.some(assignment => assignment.assignmentStatus === "Submitted")) {
            if (!window.confirm("You have unapproved Job Tags. Still proceed?")) return;
        }
        window.alert("WARNING: You will still need to verify that there are no remaining Job Tags assigned to this BTL in order to proceed with the billing process.");
        const formData = new FormData();
        formData.append("userId", selectedUserId);
        formData.append("start", selectedStart);
        formData.append("end", selectedEnd);
        const res = await myAxios({ fileName: "jobTags/getAllPDF.php", formData });
        if (!res) return;
        const urls = Array.isArray(res.data) ? (res.data as string[]) : [];
        if (!urls.length) return;
        urls.forEach((url, index) => {
            window.setTimeout(() => {
                const link = document.createElement("a");
                link.href = url;
                link.target = "_blank";
                link.rel = "noopener";
                document.body.appendChild(link);
                link.click();
                link.remove();
            }, index * 150);
        });
    };

    return (
        <div className="cssTab">
            <nav className="my-2 d-flex align-items-center flex-wrap gap-2">
                <label htmlFor="jobTagSearch" className="mb-0">
                    Search
                </label>
                <span>
                    <input
                        id="jobTagSearch"
                        className="form-control inline w-auto"
                        type="text"
                        value={filterWords}
                        onChange={event => {
                            setFilterWords(event.currentTarget.value.toLowerCase());
                            const params = new URLSearchParams(searchParams);
                            params.set("page", "1");
                            setSearchParams(params, { replace: false });
                        }}
                    />
                </span>
                <span className="d-flex align-items-center">
                    <label htmlFor="technician" className="mb-0">
                        Technician
                    </label>
                    <AsyncFormSelect
                        className="text-dark ms-2"
                        placeholder="Search technician..."
                        inputId="technician"
                        fileName="common/searchUser.php"
                        value={selectedTechnician}
                        onChange={next => {
                            const option = isSelectOption(next) ? next : null;
                            setTechnician(option);
                            setQuery(prev => ({ ...prev, userId: option?.value ?? "" }));
                        }}
                    />
                </span>
                <span className="d-flex align-items-center">
                    <label htmlFor="start" className="mb-0">
                        Start
                    </label>
                    <input
                        id="start"
                        type="date"
                        min="1900-01-01"
                        max={new MyDate().s() || "2999-12-31"}
                        className="form-control inline w-auto ms-2"
                        value={query.start}
                        onChange={event => setQuery({ ...query, start: event.currentTarget.value })}
                    />
                </span>
                <span className="d-flex align-items-center">
                    <label htmlFor="end" className="mb-0">
                        End
                    </label>
                    <input
                        id="end"
                        type="date"
                        min="1900-01-01"
                        max={new MyDate().s() || "2999-12-31"}
                        className="form-control inline w-auto ms-2"
                        value={query.end}
                        onChange={event => setQuery({ ...query, end: event.currentTarget.value })}
                    />
                </span>
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleLoad}
                    disabled={query.userId === selectedUserId && query.start === (selectedStart || defaultStartDate) && query.end === (selectedEnd || defaultEndDate)}
                >
                    Load
                </button>
                <button type="button" className="btn btn-outline-primary" onClick={handleDownloadAll}>
                    Download All
                </button>
            </nav>
            <div className="cssContent pb8rem">
                {!currentData.length ? (
                    <div className="cssLoading">Empty</div>
                ) : (
                    <div className="table-responsive">
                        <table className="table">
                            <thead className="noTableTopBorder">
                                <tr>
                                    <th>Project Name</th>
                                    <th>Planned Time</th>
                                    <th>Project Category</th>
                                    <th>Labor Category</th>
                                    <th>Department</th>
                                    <th>Assignment</th>
                                    <th>Job Tag</th>
                                    <th>Download</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentPageProjects.map((row, key) => (
                                    <tr
                                        key={key}
                                        className="pointer"
                                    >
                                        <td><Link children = {row.projectName} to = {`/Projects/${row.projectId}`}/></td>
                                        <td>
                                            <Link
                                                children = {<>
                                                    <span className="fw-bold">{row.startTime?.substring(0, 10) ?? ""}</span>
                                                    {!row.allDay ? row.startTime.substring(10, 16) : ""}
                                                    {row.allDay && row.startTime.substring(0, 10) === row.endTime.substring(0, 10) ? "" : " ~ "}
                                                    <span className="fw-bold">
                                                        {row.startTime.substring(0, 10) === row.endTime.substring(0, 10) ? "" : row.endTime.substring(0, 10)}
                                                    </span>
                                                    {!row.allDay ? row.endTime.substring(10, 16) : ""}
                                                </>}
                                                to = {`/Works/${row.workId}`}
                                            />
                                        </td>
                                        <td>{row.category}</td>
                                        <td>{row.laborCategory}</td>
                                        <td>{row.department}</td>
                                        <td><Link children = "Detail" to = {`/Assignments/${row.assignmentId}`} className="btn btn-outline-primary"/></td>
                                        <td><Link children = "Detail" to = {`/Assignments/${row.assignmentId}/JbTag`} className="btn btn-outline-primary"/></td>
                                        <td>
                                            <span
                                                id={row.jobTagFileId}
                                                className="btn btn-primary"
                                                onClick={async () => {
                                                    const formData = new FormData();
                                                    formData.append("id", row.assignmentId);
                                                    const duration = row.startTime.substring(0, 10) === row.endTime.substring(0, 10) ? row.startTime.substring(0, 10) : `from ${row.startTime.substring(0, 10)} to ${row.endTime.substring(0, 10)}`;
                                                    const resUrl = await myAxios({ fileName: "jobTags/getPDF.php", formData });
                                                    if (!resUrl) return;
                                                    try{
                                                        const response = await fetch(resUrl.data);
                                                        if(!response.ok) throw new Error("Failed to fetch file");
                                                        saveAs(await response.blob(), duration + " " + getFileNameFromUrl(resUrl.data)!);
                                                    }catch(error){
                                                        window.alert("Error downloading file:" + error);
                                                    }
                                                }}
                                            >
                                                PDF
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                {currentData.length ? (
                    <ServerFooter
                        length={filteredData.length}
                    />
                ) : null}
            </div>
        </div>
    );
}
